// games.$id.finished — the recap page.
//
// Loads the finish summary and renders, top to bottom: trophy banner,
// final standings, awards grid, round-by-round horizontal carousel, then
// closing CTAs. The backend pre-formats the intellectual-cowboy
// narration in `rounds[].story` and `awards[].reason`; we render those
// verbatim and never compute stats client-side.
import { useEffect, useRef, useState } from "react";
import {
  createFileRoute,
  Link,
  redirect,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  finishSummaryQuery,
  gameQuery,
  type FinishAward,
  type FinishRound,
  type FinishStanding,
  type FinishSummary,
} from "@/lib/queries";
import { buttonStyles } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/games/$id/finished")({
  component: FinishedPage,
  beforeLoad: async ({ context, params }) => {
    // No auth guard: open to anyone in the game. Status guard stays —
    // /finished only makes sense for finished games; anything else
    // bounces to the live scoreboard.
    const game = await context.queryClient.ensureQueryData(gameQuery(params.id));
    if (game.status !== "finished") {
      throw redirect({
        to: "/games/$id/scoreboard",
        params: { id: params.id },
      });
    }
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(finishSummaryQuery(params.id)),
});

function FinishedPage() {
  const { id } = Route.useParams();
  const summaryQ = useQuery(finishSummaryQuery(id));
  const summary = summaryQ.data;

  if (!summary) return null;

  const winner = summary.standings[0];
  const tied =
    summary.standings.length > 1 &&
    summary.standings[0].total === summary.standings[1].total;

  return (
    <section className="mx-auto mt-4 max-w-3xl space-y-6 px-2 pb-32 md:pb-10">
      <TrophyBanner
        winner={winner}
        tied={tied}
        rules={summary.game.rules}
        finishedAt={summary.game.finishedAt}
      />

      <FinalStandings standings={summary.standings} />

      <AwardsGrid awards={summary.awards} />

      <RoundCarousel rounds={summary.rounds} gameId={id} />

      <FinishedCtas gameId={id} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Trophy banner

interface TrophyBannerProps {
  winner: FinishStanding | undefined;
  tied: boolean;
  rules: FinishSummary["game"]["rules"];
  finishedAt: string;
}

function TrophyBanner({ winner, tied, rules, finishedAt }: TrophyBannerProps) {
  if (!winner) return null;
  return (
    <Card className="border-ochre-500/60 bg-ochre-500/15">
      <div className="flex flex-col gap-2">
        <div className="font-display text-4xl text-ochre-600 sm:text-5xl">
          🏆 {winner.name} took the run.
        </div>
        <div className="font-mono text-sm text-foreground">
          {winner.total} pips total{tied ? " · tied at the line" : " · the clear leader"}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">
          finished {new Date(finishedAt).toLocaleString()}
          {rules.doubleBlankPenalty
            ? " · double-blank counted 50"
            : " · double-blank counted 0"}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Final standings

function FinalStandings({ standings }: { standings: FinishStanding[] }) {
  if (standings.length === 0) return null;
  const last = standings[standings.length - 1];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Final standings</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="divide-y divide-border/40">
          {standings.map((s, i) => {
            const rank = i + 1;
            const isWinner = i === 0;
            const isCaboose = standings.length > 1 && s.playerId === last.playerId;
            return (
              <li
                key={s.playerId}
                className={cn(
                  "flex items-center justify-between gap-3 py-3",
                  isWinner && "text-ochre-600",
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "inline-flex h-7 w-7 items-center justify-center rounded-full border font-mono text-xs",
                      isWinner
                        ? "border-ochre-500 bg-ochre-500/15 text-ochre-600"
                        : "border-input bg-sand-100 text-muted-foreground",
                    )}
                    aria-label={`rank ${rank}`}
                  >
                    {rank}
                  </span>
                  <div>
                    <div className="font-display text-xl">
                      {s.name}
                      {isWinner && (
                        <span className="ml-2 align-middle text-base">🏆</span>
                      )}
                      {isCaboose && !isWinner && (
                        <span
                          className="ml-2 align-middle text-base"
                          title="caboose"
                        >
                          ⚓
                        </span>
                      )}
                      {s.isHost && (
                        <span
                          className="ml-2 font-mono text-xs text-ochre-600"
                          title="host"
                        >
                          🎩
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                      total
                    </div>
                    <div
                      className={cn(
                        "font-display text-2xl",
                        isWinner ? "text-ochre-600" : "text-foreground",
                      )}
                    >
                      {s.total}
                    </div>
                  </div>
                  {!isWinner && (
                    <span className="rounded-full border border-input bg-sand-100 px-2 py-1 font-mono text-xs text-muted-foreground">
                      +{s.gapToLeader} to leader
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Awards grid

function AwardsGrid({ awards }: { awards: FinishAward[] }) {
  if (awards.length === 0) return null;
  return (
    <section>
      <h2 className="mb-3 px-1 font-display text-2xl">The awards</h2>
      <ul className="grid gap-3 md:grid-cols-2">
        {awards.map((a) => (
          <li key={a.id}>
            <AwardCard award={a} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function AwardCard({ award }: { award: FinishAward }) {
  return (
    <Card className="h-full">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="select-none text-4xl">
          {award.icon}
        </span>
        <div className="flex-1 space-y-1">
          <div className="font-display text-lg text-rust-600">
            {award.label}
          </div>
          <div className="font-display text-2xl text-foreground">
            {award.recipientName}
          </div>
          <p className="font-mono text-sm text-muted-foreground">
            {award.reason}
          </p>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Round-by-round horizontal carousel

interface RoundCarouselProps {
  rounds: FinishRound[];
  gameId: string;
}

function RoundCarousel({ rounds, gameId }: RoundCarouselProps) {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || rounds.length === 0) return;

    const cards = cardRefs.current.filter(
      (el): el is HTMLLIElement => el !== null,
    );
    if (cards.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersection ratio that's
        // currently intersecting; that's the "active" card.
        let bestRatio = 0;
        let bestIdx: number | null = null;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const idx = cards.indexOf(e.target as HTMLLIElement);
          if (idx >= 0 && e.intersectionRatio > bestRatio) {
            bestRatio = e.intersectionRatio;
            bestIdx = idx;
          }
        }
        if (bestIdx !== null) setActiveIdx(bestIdx);
      },
      {
        root: strip,
        threshold: [0, 0.5, 0.75, 1],
      },
    );

    for (const el of cards) observer.observe(el);
    return () => observer.disconnect();
  }, [rounds.length]);

  function scrollByCardWidth(dir: -1 | 1) {
    const strip = stripRef.current;
    if (!strip) return;
    const card = cardRefs.current[activeIdx];
    const width = card?.getBoundingClientRect().width ?? strip.clientWidth;
    strip.scrollBy({ left: dir * (width + 16), behavior: "smooth" });
  }

  function jumpTo(idx: number) {
    const card = cardRefs.current[idx];
    if (!card) return;
    card.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }

  if (rounds.length === 0) return null;

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3 px-1">
        <h2 className="font-display text-2xl">Stop by stop</h2>
        <div className="hidden items-center gap-2 md:flex">
          <button
            type="button"
            aria-label="previous stop"
            onClick={() => scrollByCardWidth(-1)}
            className={cn(
              buttonStyles("ghost", "sm"),
              "h-9 w-9 px-0",
            )}
            disabled={activeIdx <= 0}
          >
            ←
          </button>
          <span className="font-mono text-xs text-muted-foreground">
            stop {rounds[activeIdx]?.number ?? 1} / {rounds.length}
          </span>
          <button
            type="button"
            aria-label="next stop"
            onClick={() => scrollByCardWidth(1)}
            className={cn(
              buttonStyles("ghost", "sm"),
              "h-9 w-9 px-0",
            )}
            disabled={activeIdx >= rounds.length - 1}
          >
            →
          </button>
        </div>
      </div>

      <div
        ref={stripRef}
        className="-mx-2 flex snap-x snap-mandatory gap-4 overflow-x-auto px-2 pb-3"
        style={{ scrollSnapType: "x mandatory" }}
      >
        <ul className="contents">
          {rounds.map((r, i) => (
            <li
              key={r.number}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className="shrink-0 snap-center"
              style={{
                flexBasis: "85%",
                maxWidth: "28rem",
              }}
            >
              <StopCard round={r} gameId={gameId} />
            </li>
          ))}
        </ul>
      </div>

      <div
        role="tablist"
        aria-label="stops"
        className="mt-2 flex flex-wrap items-center justify-center gap-1.5"
      >
        {rounds.map((r, i) => (
          <button
            key={r.number}
            role="tab"
            aria-selected={i === activeIdx}
            aria-label={`stop ${r.number}`}
            onClick={() => jumpTo(i)}
            className={cn(
              "h-2 w-2 rounded-full transition-colors",
              i === activeIdx
                ? "bg-rust-500"
                : "bg-input hover:bg-sand-200",
            )}
          />
        ))}
      </div>
    </section>
  );
}

interface StopCardProps {
  round: FinishRound;
  gameId: string;
}

function StopCard({ round, gameId }: StopCardProps) {
  const photoCells = round.cells.filter((c) => c.hasPhoto);
  return (
    <Card className="h-full md:!flex-[0_0_28rem] md:max-w-[28rem]">
      <CardHeader>
        <div className="font-mono text-xs uppercase tracking-wider text-ochre-600">
          Stop {round.number}
        </div>
      </CardHeader>
      <CardContent>
        <p className="font-display italic text-xl leading-snug text-foreground">
          {round.story}
        </p>

        <ul className="mt-4 divide-y divide-border/40">
          {round.cells.map((c, i) => (
            <li
              key={c.playerId}
              className={cn(
                "flex items-baseline justify-between gap-3 py-1.5 font-mono text-sm",
                i === 0 && "text-ochre-600",
              )}
            >
              <span className="font-display text-base">
                {c.name}
                {i === 0 && (
                  <span className="ml-1 text-base">🏆</span>
                )}
              </span>
              <span
                className={cn(
                  "font-mono text-base",
                  i === 0 ? "text-ochre-600" : "text-foreground",
                )}
              >
                {c.total}
              </span>
            </li>
          ))}
        </ul>

        {photoCells.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {photoCells.map((c) => (
              <Link
                key={c.scoreId}
                to="/games/$id/scores/$scoreId/audit"
                params={{ id: gameId, scoreId: c.scoreId }}
                aria-label={`open ${c.name}'s score`}
                className="block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <img
                  src={`/api/v1/games/${gameId}/scores/${c.scoreId}/photo`}
                  alt={`${c.name} stop ${round.number}`}
                  width={64}
                  height={64}
                  loading="lazy"
                  className="h-16 w-16 rounded-md border border-rust-500/40 object-cover"
                  style={{ width: 64, height: 64 }}
                />
              </Link>
            ))}
          </div>
        )}

        <div className="mt-4 font-mono text-[11px] text-muted-foreground">
          avg {round.avg} · range {round.low}–{round.high}
          {round.stdDev > 0 && ` · σ ${round.stdDev}`}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Closing CTAs

function FinishedCtas({ gameId }: { gameId: string }) {
  return (
    <>
      {/* Desktop */}
      <div className="hidden items-center justify-end gap-3 md:flex">
        <Link
          to="/games/$id/scoreboard"
          params={{ id: gameId }}
          className={buttonStyles("ghost", "default")}
        >
          Back to scoreboard
        </Link>
        <Link
          to="/games/create"
          className={buttonStyles("primary", "lg")}
        >
          Start a new game
        </Link>
      </div>

      {/* Mobile pinned-bottom */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex flex-col gap-2 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
        <Link
          to="/games/create"
          className={buttonStyles("primary", "lg", "w-full")}
        >
          Start a new game
        </Link>
        <Link
          to="/games/$id/scoreboard"
          params={{ id: gameId }}
          className="text-center font-mono text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          back to scoreboard
        </Link>
      </div>

    </>
  );
}
